import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, ReplaySubject, switchMap, take, takeUntil, tap } from 'rxjs';

import { SiteState } from 'src/app/shared/models';
import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { selectCropYear } from 'src/app/store/selectors';
import {
  GenericPlantingQuestions,
  GenericPlantingQuestionsRequest,
  ProcessedAnswers,
  ProcessedAnswersWithForm,
} from 'src/app/tga/shared/models/models';
import { fetchGenericPlantingQuestions, postGenericPlantingQuestions } from 'src/app/tga/store/tga.actions';
import { selectGenericPlantingQuestions } from 'src/app/tga/store/tga.selectors';

import { GenericPlantingQuestionsService } from './generic-planting-questions.service';
import { TgaInputType, TGAQuestion } from '../../../models/question-models';
import { BaseGenericQuestionnaireComponent } from '../base-generic-questionnaire/base-generic-questionnaire.component';

@Component({
  selector: 'app-generic-planting-questionnaire',
  templateUrl: './generic-planting-questionnaire.component.html',
  imports: [BaseGenericQuestionnaireComponent, TranslatePipe],
})
export class GenericPlantingQuestionnaireComponent implements OnInit, OnDestroy {
  protected readonly isCropYearReadOnly = inject(CropYearService).isReadOnly;
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly genericPlantingQuestionsService = inject(GenericPlantingQuestionsService);
  private readonly route = inject(ActivatedRoute);
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);

  cropYear: number | null = null;
  formGroup: FormGroup = new FormGroup({});
  plantingQuestions: TGAQuestion<TgaInputType>[] = [];
  formReady = signal(false);
  private previousFormValues: Record<string, unknown> = {};
  private hasShownConfirmation = false;

  destroyed$ = new ReplaySubject<boolean>(1);
  readonly selectGenericPlantingQuestions$ = this.store.select(selectGenericPlantingQuestions);
  private readonly selectedCropYear$ = this.store.select(selectCropYear);

  readonly params$ = this.createParamsStream();
  readonly questions$ = this.createQuestionsStream();

  ngOnInit(): void {
    this.initializeParamsAndQuestions();
    this.initializeQuestionsAndForm();
  }

  answersUpdated(data: ProcessedAnswersWithForm): void {
    if (this.isCropYearReadOnly()) return;

    this.selectGenericPlantingQuestions$.pipe(take(1)).subscribe((questionsWithStatus) => {
      const shouldShowDialog = questionsWithStatus?.hasFilledInSpecific === true && !this.hasShownConfirmation;

      if (shouldShowDialog) {
        this.dialogService
          .confirm({
            title: this.locale.translate('TGA.Questionnaire.Generic.OverwriteConfirmMessage'),
            text: this.locale.translate('TGA.Questionnaire.Generic.OverwriteConfirmDescription'),
            pushHistoryState: false,
          })
          .subscribe((result) => {
            if (this.isCropYearReadOnly()) return;

            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.hasShownConfirmation = true;
              if (data.formGroup.valid) {
                this.validateAndSubmit(data);
              }
            } else {
              // User cancelled - revert form to previous values
              this.revertFormValues(data.formGroup);
            }
          });
      } else {
        this.validateAndSubmit(data);
      }
    });
  }

  private validateAndSubmit(data: ProcessedAnswersWithForm) {
    if (this.isCropYearReadOnly()) return;

    const { answers: processedAnswers, formGroup } = data;

    this.markFormGroupTouched(formGroup);

    if (formGroup.valid) {
      this.submitAnswers(processedAnswers);
    }
  }

  private submitAnswers(processedAnswers: ProcessedAnswers): void {
    if (this.isCropYearReadOnly()) return;

    if (this.cropYear) {
      const improvementTopics = this.genericPlantingQuestionsService.processImprovementTopics(processedAnswers);
      const longStorageValue = processedAnswers['longStorageExpected'] as string | null;
      let longStorageExpected: boolean | null = null;

      if (longStorageValue === 'true') {
        longStorageExpected = true;
      } else if (longStorageValue === 'false') {
        longStorageExpected = false;
      }

      const plantingQuestions: GenericPlantingQuestions = {
        ...improvementTopics,
        longStorageExpected,
      };

      const answersPayload: GenericPlantingQuestionsRequest = {
        ...plantingQuestions,
        cropYear: new Date().getFullYear(),
      };

      this.store.dispatch(
        postGenericPlantingQuestions({
          answers: answersPayload,
        }),
      );

      this.unsavedChangesService.markFormCompleted();
    }
  }

  private revertFormValues(formGroup: FormGroup): void {
    formGroup.patchValue(this.previousFormValues, { emitEvent: false });
    this.hasShownConfirmation = false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private createParamsStream() {
    return this.route.params.pipe(
      takeUntil(this.destroyed$),
      map(this.extractRouteParams.bind(this)),
      filter(this.validateRouteParams),
    );
  }

  private extractRouteParams(params: { [key: string]: string | undefined }) {
    this.cropYear = Number.parseInt(params['cropYear'] || new Date().getFullYear().toString(), 10);
    return {
      cropYear: this.cropYear,
    };
  }

  private validateRouteParams(params: { cropYear: number }) {
    return !Number.isNaN(params.cropYear);
  }

  private createQuestionsStream() {
    return combineLatest([this.selectGenericPlantingQuestions$]).pipe(
      tap(() => {
        this.genericPlantingQuestionsService.initializePlantingQuestions();
      }),
      map(() => {
        return this.genericPlantingQuestionsService.initValues();
      }),
    );
  }

  private initializeParamsAndQuestions(): void {
    this.params$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() =>
          this.questions$.pipe(
            take(1),
            tap(() => this.checkAndFetchMissingQuestions()),
          ),
        ),
      )
      .subscribe();
  }

  private checkAndFetchMissingQuestions(): void {
    this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(fetchGenericPlantingQuestions({ cropYear }));
    });
  }

  private initializeQuestionsAndForm(): void {
    this.questions$
      .pipe(
        takeUntil(this.destroyed$),
        filter((questions) => questions.length > 0),
      )
      .subscribe((questions) => {
        this.plantingQuestions = questions;
        this.buildForm(questions);
      });
  }

  private buildForm(questions: TGAQuestion<TgaInputType>[]): void {
    if (questions.length === 0) {
      return;
    }

    const group: { [key: string]: AbstractControl } = {};

    for (const question of questions) {
      const initialValue: TgaInputType | Date | undefined = question.value;

      const validators = question.required ? [Validators.required] : [];

      group[question.key] = new FormControl(initialValue ?? null, validators);
    }

    this.formGroup = new FormGroup(group);
    this.previousFormValues = { ...this.formGroup.value };
    this.formReady.set(true);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
